import React, { useId, useMemo } from 'react';
import SourceChip from './ui/SourceChip';
import type { Source } from '@/types';
import { categoryDisplayName } from '@/utils/sourceDisplayName';

export type SourceSelectionKey = 'id' | 'name';

export function normalizeSourceId(id: string): string {
  return id.toLowerCase();
}

function toSelectedSet(selected: Set<string> | readonly string[]): Set<string> {
  return selected instanceof Set ? selected : new Set(selected);
}

function sourceKey(source: Source, selectionKey: SourceSelectionKey): string {
  return selectionKey === 'id' ? normalizeSourceId(source.id) : source.name;
}

export type SourceSelectorProps = {
  sources: Source[];
  selected: Set<string> | readonly string[];
  onToggle: (key: string) => void;
  selectionKey?: SourceSelectionKey;
  onSelectAll?: () => void;
  onClearAll?: () => void;
  disabled?: boolean;
  legend?: string;
  /** When true, legend is used for aria-label only (parent heading provides visible label). */
  hideLegend?: boolean;
  helperText?: string;
  useFieldset?: boolean;
  summaryFormat?: 'fraction' | 'selected';
  emptyHint?: string;
  groupByCategory?: boolean;
  className?: string;
  scrollable?: boolean;
};

export default function SourceSelector({
  sources,
  selected,
  onToggle,
  selectionKey = 'name',
  onSelectAll,
  onClearAll,
  disabled = false,
  legend = 'Sources',
  hideLegend = false,
  helperText,
  useFieldset = true,
  summaryFormat = 'fraction',
  emptyHint,
  groupByCategory = false,
  className = '',
  scrollable = false,
}: SourceSelectorProps) {
  const legendId = useId();
  const selectedSet = toSelectedSet(selected);
  const enabledSources = useMemo(() => sources.filter((s) => s.is_enabled), [sources]);

  const selectedEnabledCount = useMemo(
    () => enabledSources.filter((s) => selectedSet.has(sourceKey(s, selectionKey))).length,
    [enabledSources, selectedSet, selectionKey],
  );

  const grouped = useMemo(() => {
    if (!groupByCategory) return [{ category: null as string | null, items: sources }];
    const map = new Map<string, Source[]>();
    for (const s of sources) {
      const cat = s.category || 'other';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(s);
    }
    return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
  }, [sources, groupByCategory]);

  const showActions = !disabled && enabledSources.length > 0 && (onSelectAll || onClearAll);

  const summaryText =
    summaryFormat === 'selected'
      ? `${enabledSources.length} available · ${selectedEnabledCount} selected`
      : `${enabledSources.length} available · ${selectedEnabledCount} of ${enabledSources.length} selected`;

  const content = (
    <>
      {sources.length === 0 && emptyHint ? (
        <p className="source-selector__empty muted">{emptyHint}</p>
      ) : null}

      {helperText ? <p className="source-selector__helper muted">{helperText}</p> : null}

      <div
        className={['source-selector__chips-wrap', scrollable ? 'source-selector__chips-wrap--scroll' : '']
          .filter(Boolean)
          .join(' ')}
      >
        {grouped.map(({ category, items }) => (
          <div key={category ?? 'all'} className="source-chip-group">
            {category && groupByCategory ? (
              <div className="source-chip-group__label">{categoryDisplayName(category)}</div>
            ) : null}
            <div className="sources-chips sources-chips--inline" role="group">
              {items.map((s) => {
                const key = sourceKey(s, selectionKey);
                const on = selectedSet.has(key);
                const chipDisabled = disabled || !s.is_enabled;
                return (
                  <SourceChip
                    key={s.id}
                    source={s.name}
                    enabled={s.is_enabled}
                    mode="toggle"
                    selected={on}
                    disabled={chipDisabled}
                    onClick={() => onToggle(key)}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {showActions ? (
        <div className="source-selector__actions">
          {onSelectAll ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onSelectAll}>
              Select all
            </button>
          ) : null}
          {onClearAll ? (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onClearAll}>
              Clear all
            </button>
          ) : null}
        </div>
      ) : null}

      {enabledSources.length > 0 ? (
        <p className="source-selector__summary">{summaryText}</p>
      ) : null}
    </>
  );

  const groupLabelProps = hideLegend
    ? { role: 'group' as const, 'aria-label': legend }
    : { role: 'group' as const, 'aria-labelledby': legendId };

  if (!useFieldset) {
    return (
      <div
        className={['source-selector', 'source-selector--group', className].filter(Boolean).join(' ')}
        {...groupLabelProps}
      >
        {!hideLegend ? (
          <div id={legendId} className="source-selector__heading">
            {legend}
          </div>
        ) : null}
        {content}
      </div>
    );
  }

  return (
    <fieldset
      className={['source-selector', className].filter(Boolean).join(' ')}
      disabled={disabled}
      {...(hideLegend ? { 'aria-label': legend } : { 'aria-labelledby': legendId })}
    >
      {!hideLegend ? (
        <legend id={legendId} className="source-selector__legend">
          {legend}
        </legend>
      ) : null}
      {content}
    </fieldset>
  );
}
